import { ChangeDetectionStrategy, Component } from "@angular/core";
import { CommonModule } from "@angular/common";

import { Observable, Subject } from "rxjs";

import { LgChartsModule, LgColorTypes, LgOwnColorConfiguration } from "@logex/framework/lg-charts";
import { LgLocalizationModule, useTranslationNamespace } from "@logex/framework/lg-localization";
import { LgFormatIntegerPipe, UiCoreModule } from "@logex/framework/ui-core";

import { IOverlayPopup } from "@codman/shared/ui-overlay";
import { IComparisonType, IConnectProvider } from "@codman/shared/feature-benchmarking";

export interface ComparisonPopupInfoInitProps {
    patientsCount: PatientsCount;
    analysisUnit?: string;
    showPatientList: boolean;
    showTrend: boolean;
    showConnect: boolean;
    color: string;
    title: string;
    availableConnectProviders: IConnectProvider[];
    selectedConnectProviders: number[];
    comparisonType?: IComparisonType | null;
    multiDataIndex: number;
}

export type ComparisonPopupInfoClickEventTrend = {
    type: "trend";
    multiDataIndex: number;
    comparisonType: IComparisonType;
};
export type ComparisonPopupInfoClickEventList = {
    type: "list";
    multiDataIndex: number;
};
export type ComparisonPopupInfoClickEventConnect = {
    type: "connect";
    connectProviders: number[];
    multiData?: boolean;
};

export type ComparisonPopupInfoClickEvent =
    | ComparisonPopupInfoClickEventTrend
    | ComparisonPopupInfoClickEventList
    | ComparisonPopupInfoClickEventConnect;

export interface PatientsCount {
    numerator?: number;
    denominator?: number;
}

@Component({
    selector: "codman-comparison-picker-popup-info",
    templateUrl: "./comparison-picker-popup-info.component.html",
    styleUrls: ["./comparison-picker-popup-info.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [useTranslationNamespace("APP._Shared.ComparisonPicker")],
    standalone: true,
    imports: [
        CommonModule,
        UiCoreModule,
        LgChartsModule,
        LgLocalizationModule,
        LgFormatIntegerPipe,
    ],
})
export class SharedComparisonPopupInfoComponent
    implements IOverlayPopup<ComparisonPopupInfoInitProps, ComparisonPopupInfoClickEvent>
{
    _patientsCount: PatientsCount | undefined;
    _analysisUnit?: string;
    _title = "";
    _showPatientList?: boolean;
    _showTrend?: boolean;
    _showConnect?: boolean;
    _availableConnectProviders: IConnectProvider[] | null = [];
    _selectedConnectProviders: number[] | null | undefined;
    _comparisonType: IComparisonType | null | undefined = "basic";
    _multiDataIndex: number = 0;

    readonly _clickEvent$ = new Subject<ComparisonPopupInfoClickEvent>();
    readonly LgColorTypes = LgColorTypes;

    _colorConfiguration: LgOwnColorConfiguration = {
        colorType: LgColorTypes.Own,
        colors: [""],
    };

    initialize(config: ComparisonPopupInfoInitProps): Observable<ComparisonPopupInfoClickEvent> {
        this._patientsCount = config.patientsCount;
        this._analysisUnit = config.analysisUnit;
        this._showPatientList = config.showPatientList;
        this._showTrend = config.showTrend;
        this._showConnect = config.showConnect;
        this._availableConnectProviders = config.availableConnectProviders;
        this._selectedConnectProviders = config.selectedConnectProviders;
        this._title = config.title;
        this._colorConfiguration.colors = [config.color];
        this._comparisonType = config.comparisonType ?? "basic";
        this._multiDataIndex = config.multiDataIndex;
        return this._clickEvent$.asObservable();
    }

    _updatePosition(): void {
        //
    }

    _onShowTrend(): void {
        this._clickEvent$.next({
            type: "trend",
            multiDataIndex: this._multiDataIndex,
            comparisonType: this._comparisonType ?? "basic",
        });
        this._clickEvent$.complete();
    }

    _onShowPatientsList(): void {
        this._clickEvent$.next({ type: "list", multiDataIndex: this._multiDataIndex });
        this._clickEvent$.complete();
    }

    _onHide(): void {
        this._clickEvent$.complete();
    }

    _onItemChanged(id: number): void {
        if (this._selectedConnectProviders?.includes(id)) {
            this._selectedConnectProviders = this._selectedConnectProviders?.filter(
                providerId => providerId !== id,
            );
        } else {
            this._selectedConnectProviders?.push(id);
        }
    }

    _onAllItemsChanged(): void {
        if (this._selectedConnectProviders?.length) {
            this._selectedConnectProviders = [];
        } else {
            this._selectedConnectProviders = this._availableConnectProviders?.map(
                provider => provider.id,
            );
        }
    }

    _onApply(): void {
        if (this._selectedConnectProviders?.length === 0) {
            this._selectedConnectProviders = this._availableConnectProviders?.map(
                provider => provider.id,
            );
        }
        this._clickEvent$.next({
            type: "connect",
            connectProviders: this._selectedConnectProviders ?? [],
            multiData: this._comparisonType === "multiData",
        });
        this._clickEvent$.complete();
    }

    _onCancel(): void {
        this._clickEvent$.complete();
    }
}
